import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Calendar,
  QrCode,
  ChevronRight,
  Clock,
  Users,
  CircleHelp,
  Bell,
  ScanLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCommunity } from "@/contexts/CommunityContext";
import { useTheme } from "@/lib/theme";
import heroLight from "@/assets/hero-light.png";
import heroDark from "@/assets/hero-dark.png";

function formatDate(date: Date | string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime(date: Date | string) {
  return new Date(date).toLocaleTimeString("es-AR", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Buen día";
  if (hour < 18) return "Buenas tardes";
  return "Buenas noches";
}

const residentQuickAccess = [
  {
    label: "Reservar",
    icon: Calendar,
    to: "/amenities" as const,
  },
  {
    label: "Invitación",
    icon: QrCode,
    to: "/invitations" as const,
  },
  {
    label: "Tickets",
    icon: CircleHelp,
    to: "/tickets" as const,
  },
  {
    label: "Novedades",
    icon: Bell,
    to: "/notifications" as const,
  },
];

const securityQuickAccess = [
  {
    label: "Acceso",
    icon: ScanLine,
    to: "/guard" as const,
  },
  {
    label: "Novedades",
    icon: Bell,
    to: "/notifications" as const,
  },
];

export function HomePage() {
  const { selectedCommunityId } = useCommunity();
  const { effectiveTheme } = useTheme();
  const { data: user } = trpc.user.me.useQuery();

  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();

  const { data: amenities, isLoading: amenitiesLoading } =
    trpc.amenity.getAmenities.useQuery(
      { communityId: selectedCommunityId || "" },
      { enabled: !!selectedCommunityId },
    );

  const { data: invitations, isLoading: invitationsLoading } =
    trpc.invitation.getMyInvitations.useQuery(
      { communityId: selectedCommunityId || "" },
      { enabled: !!selectedCommunityId },
    );

  const currentCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );

  const isSecurityGuard = currentCommunity?.role === "SECURITY";

  const upcomingReservations = amenities
    ?.flatMap((a) =>
      a.myReservations
        .filter(
          (r) => r.status === "CONFIRMED" && new Date(r.startTime) > new Date(),
        )
        .map((r) => ({ ...r, amenityName: a.name })),
    )
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );

  const activeInvitations = invitations?.filter(
    (i) =>
      i.status === "ACTIVE" &&
      i.validUntil &&
      new Date(i.validUntil) > new Date(),
  );

  const upcomingCount = upcomingReservations?.length ?? 0;
  const activeGuestsCount = activeInvitations?.length ?? 0;

  const isLoading = amenitiesLoading || invitationsLoading;
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      {/* Greeting */}
      <div className="mb-4">
        {currentCommunity && (
          <p className="mb-1 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
            {currentCommunity.name}
          </p>
        )}
        <h1 className="text-2xl font-bold md:text-3xl">
          {getGreeting()}
          {firstName ? `, ${firstName}` : ""}
        </h1>
      </div>

      {/* Full-width image banner */}
      <div className="overflow-hidden rounded-2xl">
        <img
          src={effectiveTheme === "dark" ? heroDark : heroLight}
          alt="Barrio"
          className="h-44 w-full object-cover md:h-56"
        />
      </div>

      {/* Stat boxes (resident/admin only) */}
      {!isSecurityGuard && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Card className="border-0 bg-primary px-4 py-3 text-primary-foreground">
            <p className="text-2xl font-bold">{upcomingCount}</p>
            <p className="text-xs opacity-75">Próximas reservas</p>
          </Card>
          <Card className="border-0 bg-primary px-4 py-3 text-primary-foreground">
            <p className="text-2xl font-bold">{activeGuestsCount}</p>
            <p className="text-xs opacity-75">Invitados activos</p>
          </Card>
        </div>
      )}

      {/* Quick Access */}
      <div className="mt-6">
        <h2 className="mb-3 text-sm font-semibold">Acceso rápido</h2>
        <div
          className={cn(
            "grid gap-3",
            isSecurityGuard ? "grid-cols-2" : "grid-cols-4",
          )}
        >
          {(isSecurityGuard ? securityQuickAccess : residentQuickAccess).map(
            (item) => (
              <Link key={item.label} to={item.to}>
                <div className="flex flex-col items-center gap-2 rounded-2xl bg-card p-4 shadow-sm transition-colors hover:bg-accent">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="text-center text-xs font-medium">
                    {item.label}
                  </span>
                </div>
              </Link>
            ),
          )}
        </div>
      </div>

      {!isSecurityGuard &&
        (isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {/* Upcoming Reservations */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <CardTitle className="text-base">
                      Próximas reservas
                    </CardTitle>
                  </div>
                  <Link to="/amenities">
                    <Button variant="ghost" size="sm" className="text-xs">
                      Ver todo
                      <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {upcomingReservations && upcomingReservations.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingReservations.slice(0, 3).map((reservation) => (
                      <div
                        key={reservation.id}
                        className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium">
                            {reservation.amenityName}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatDate(reservation.startTime)},{" "}
                            {formatTime(reservation.startTime)} -{" "}
                            {formatTime(reservation.endTime)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Todavía no tenés reservas.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Guest Invitations */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <QrCode className="h-4 w-4" />
                    </div>
                    <CardTitle className="text-base">Invitaciones</CardTitle>
                  </div>
                  <Link to="/invitations">
                    <Button variant="ghost" size="sm" className="text-xs">
                      Ver todo
                      <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {activeInvitations && activeInvitations.length > 0 ? (
                  <div className="space-y-3">
                    {activeInvitations.slice(0, 3).map((invitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-medium">
                            {invitation.guestName}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Users className="h-3 w-3" />
                            {invitation.validUntil &&
                              `Válida hasta ${formatDate(invitation.validUntil)}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No hay invitaciones activas.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        ))}
    </div>
  );
}
